/**
 * What the save control's script asks `/user/ci/words/`, and what comes back.
 *
 * Apart from `save-word.ts`, which is the page and the presses on it. This half
 * is the two requests and the shape of an answer, and it is separate because a
 * browser has to treat what it fetched as unknown until it has looked. Every
 * field below is read one at a time rather than trusted.
 *
 * The route is the handler's `CiWords`, and `src/user/ci-words.ts` says why it
 * answers JSON rather than a page.
 */

/** Where a reader's words live, and where a press without a script goes. */
export const ciPath = "/user/ci/";

/** Where the script asks about words and posts one. */
const ciWordsPath = "/user/ci/words/";

/** The field carrying the headword, on the form and on the query string. */
export const wordField = "word";

/** The parameter carrying the words a page is asking about. */
const wordsParameter = "words";

/** What separates them, which a headword can never carry. */
const wordsSeparator = ",";

/** The parameter naming a word the cap would not let through. */
const refusedParameter = "full";

/** What the route answers, before anything has looked at it. */
interface WordsAnswer {
  readonly saved?: unknown;
  readonly full?: unknown;
}

/** What one press did, as the page reads it. */
export interface PressResult {
  /** Whether the reader now holds the word. */
  readonly held: boolean;

  /** Whether the reader has no session, and is to be offered an account. */
  readonly signedOut: boolean;

  /** Whether the save was refused at the cap. */
  readonly full: boolean;
}

/** Where a reader is sent when the cap would not let a word through. */
export function refusedLocation(word: string): string {
  return `${ciPath}?${refusedParameter}=${encodeURIComponent(word)}`;
}

/**
 * Which of the words on this page the reader holds.
 *
 * Nothing where the answer is not a list of words, which is a reader with no
 * session and anything else this could not read. The page draws every control
 * offering to save, so an answer nobody understood leaves it as it is.
 */
export async function askHeldWords(
  fetched: typeof fetch,
  words: readonly string[],
): Promise<readonly string[] | undefined> {
  const answer = await fetched(
    `${ciWordsPath}?${wordsParameter}=${encodeURIComponent(words.join(wordsSeparator))}`,
  );
  const held = ((await answer.json()) as WordsAnswer).saved;

  return Array.isArray(held) ? (held as readonly string[]) : undefined;
}

/** Turn one word over, and say what happened to it. */
export async function toggleSavedWord(
  fetched: typeof fetch,
  word: string,
): Promise<PressResult> {
  const answer = await fetched(ciWordsPath, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ [wordField]: word }).toString(),
  });

  const reported = (await answer.json()) as WordsAnswer;
  const saved = reported.saved;

  return {
    held: Array.isArray(saved) && saved.includes(word),
    signedOut: !Array.isArray(saved),
    full: reported.full === true,
  };
}
